const STORAGE_KEY = 'elora_analytics_track_log';

const FREQUENCY_LIMIT = 10;
const FREQUENCY_WINDOW_MS = 1000;
const VOLUME_LIMIT = 100;
const VOLUME_WINDOW_MS = 15 * 60 * 1000;

// localStorage is shared across tabs but reads/writes aren't atomic across
// them, so a read-compute-write can race with another tab doing the same
// and silently lose an update. We guard against that with an optimistic
// concurrency check (re-read immediately before writing, retry on
// conflict) rather than a blind write.
const MAX_WRITE_ATTEMPTS = 5;

const readLogRaw = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error reading analytics throttle log:', error);
    return null;
  }
};

const parseLog = (raw: string | null): number[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is number => typeof entry === 'number');
  } catch (error) {
    console.error('Error parsing analytics throttle log:', error);
    return [];
  }
};

const writeLog = (log: number[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch (error) {
    console.error('Error writing analytics throttle log:', error);
  }
};

export const isTrackThrottled = (now: number = Date.now()): boolean => {
  for (let attempt = 0; attempt < MAX_WRITE_ATTEMPTS; attempt += 1) {
    const before = readLogRaw();
    const log = parseLog(before);
    const withinVolumeWindow = log.filter(t => t > now - VOLUME_WINDOW_MS);
    const withinFrequencyWindow = withinVolumeWindow.filter(
      t => t > now - FREQUENCY_WINDOW_MS
    );

    const throttled =
      withinFrequencyWindow.length >= FREQUENCY_LIMIT ||
      withinVolumeWindow.length >= VOLUME_LIMIT;

    const nextLog = throttled
      ? withinVolumeWindow
      : [...withinVolumeWindow, now];

    // Another tab may have written to the log since `before` was read.
    // Retry with fresh data instead of overwriting its update.
    if (readLogRaw() !== before) {
      continue;
    }

    writeLog(nextLog);
    return throttled;
  }

  // Contention persisted across every retry (very unlikely) — fail open
  // rather than silently dropping the event.
  return false;
};
